import { create } from 'zustand'
import { authApi } from '../api/client'
import type { User } from '../types/api'

// B1-#12: lazily import so there's no circular-dep issue at module load time
function syncTheme(user: User | null | undefined) {
  if (!user) return
  import('./theme').then(m => m.useThemeStore.getState().applyServerTheme((user as any).theme)).catch(() => {})
}

interface AuthStore {
  user: User | null
  initialized: boolean  // true once init() has completed (success or failure)
  // Token is stored in HttpOnly cookie by the backend — never in localStorage
  token: string | null
  login: (email: string, password: string) => Promise<{ requires_mfa: boolean; partial_token: string | null }>
  logout: () => Promise<void>
  init: () => Promise<void>
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  initialized: false,
  token: null,   // Token lives in HttpOnly cookie — not accessible to JavaScript (FINDING-014)

  login: async (email, password) => {
    const res = await authApi.login(email, password)
    const { requires_mfa, partial_token, user } = res.data
    if (!requires_mfa && user) {
      // Cookie was set by the backend — just store the user object in memory
      localStorage.setItem('ds_session', '1')  // non-sensitive hint: a session cookie exists
      set({ user, initialized: true })
      syncTheme(user)  // B1-#12: apply server theme preference
    }
    return { requires_mfa, partial_token }
  },

  logout: async () => {
    try { await authApi.logout() } catch { /* ignore */ }
    localStorage.removeItem('ds_session')  // clear session hint so init() skips /me on next visit
    set({ user: null, token: null, initialized: true })
    window.location.href = '/login'
  },

  init: async () => {
    // Only call /auth/me if a session cookie is expected to exist.
    // Without this guard, every unauthenticated page load fires GET /auth/me → 401
    // which the browser logs as a red console error even though the app handles it.
    // The HttpOnly cookie itself can't be read by JS, so we use a non-sensitive
    // localStorage flag ('ds_session') set on login and cleared on logout as a proxy.
    if (!localStorage.getItem('ds_session')) {
      set({ user: null, initialized: true })
      return
    }
    try {
      const res = await authApi.me()
      localStorage.setItem('ds_session', '1')  // keep flag in sync
      set({ user: res.data, initialized: true })
      syncTheme(res.data)  // B1-#12: apply server theme preference on page reload
    } catch {
      // Session cookie expired or invalid — clear flag and stay logged out
      localStorage.removeItem('ds_session')
      set({ user: null, token: null, initialized: true })
    }
  },

  setUser: (user: User) => set({ user }),
}))
